import { Surface, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { MarketingCharacteristics } from '@app/constants';
import { useScreenResolution } from '@app/hooks';

import { getCharacteristicValue } from '@mfe/shared/utils';

import TermsAndSubscribeButton from '../TermsAndSubscribe';
import CartAddonName from './CartAddonName';
import EmptyCart from './EmptyCart';
import { getPriceAndDiscount } from '../../utils';
import { CartProps, GetPriceAndDiscountResult } from '../../types';

import styles from '../../styles/cart.module.scss';

const AddonCart = (props: CartProps) => {
  if (!props.addon.is_grouped_product) {
    return <PopulatedCart {...props} />;
  }

  return props.selectedEasyCareProduct ? (
    <PopulatedCart {...props} addon={props.selectedEasyCareProduct} />
  ) : (
    <EmptyCart {...props} />
  );
};

const PopulatedCart = ({
  addon,
  submitButtonLabel,
  isSubmitDisabled,
  handleClick,
  displayTC,
  isLastStep,
}: CartProps) => {
  const { t } = useTranslation('ShopAddons');

  const { name, kind } = addon;
  const characteristics = addon.marketing_copy?.translations?.[0]?.characteristics ?? [];
  const offerName = getCharacteristicValue(characteristics, MarketingCharacteristics.OFFER_NAME);
  const displayName = offerName ?? name;

  const {
    price,
    oldPrice,
    totalPromotionDuration,
    promotions = [],
  }: GetPriceAndDiscountResult = getPriceAndDiscount(addon);

  const { isLarge, isExtraLarge } = useScreenResolution();
  const isDesktop = isLarge || isExtraLarge;

  const hasPromotions = promotions.length > 0;
  const priceColor = hasPromotions ? 'success' : 'regular';
  const hasActivePromotions = oldPrice !== undefined && totalPromotionDuration !== undefined;

  return (
    <div className={styles['cart']}>
      <Surface radius="16px" p={isDesktop ? '24px' : undefined} data-cy="cart-content">
        <div className={styles['vertical-spacing']}>
          <Txt variant="bodyLargeBold" color="subtle">
            {t('cart.title')}
          </Txt>
          <div className={styles['content']}>
            <CartAddonName name={displayName} kind={kind} />
            <div className={styles['subscription-price']}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Txt variant="bodySmallRegular" color="regular" style={{ alignSelf: 'self-start' }}>
                  {t('cart.subscription')}
                </Txt>
                <div>
                  <Txt variant="bodySmallBold" color={priceColor} style={{ textAlign: 'right' }}>
                    {price === 0 ? t('cart.free') : t('cart.pricePerMonth', { price })}
                  </Txt>
                  {hasActivePromotions && (
                    <Txt variant="smallItalic" color="subtle">
                      {t('cart.priceAfterDiscount', {
                        price: oldPrice,
                        count: totalPromotionDuration,
                      })}
                    </Txt>
                  )}
                </div>
              </div>
            </div>
            <div className={styles['total']}>
              <Txt variant="heading6" color="regular">
                {t('cart.total')}
              </Txt>
              <Txt variant="heading6" color="regular" style={{ textAlign: 'right' }}>
                {price === 0 ? t('cart.free') : t('cart.pricePerMonth', { price })}
                {hasActivePromotions && (
                  <Txt component="p" variant="smallItalic" color="subtle">
                    {t('cart.priceAfterDiscount', {
                      price: oldPrice,
                      duration: totalPromotionDuration,
                    })}
                  </Txt>
                )}
                <Txt component="p" variant="smallRegular" color="regular">
                  {t('cart.taxes')}
                </Txt>
              </Txt>
            </div>
          </div>
          <TermsAndSubscribeButton
            kind={kind}
            displayTC={displayTC}
            handleClick={handleClick}
            isSubmitDisabled={isSubmitDisabled}
            submitButtonLabel={submitButtonLabel}
            isLastStep={isLastStep}
          />
        </div>
      </Surface>
    </div>
  );
};

export default AddonCart;
